/**
 * Picks the right engine simulator for a pathogen: vector-borne pathogens
 * (transmission.modes includes a vector-* mode) run on the host-vector
 * Ross-Macdonald model (real EIP/vector dynamics); everything else runs on
 * SEIRD. Both return an EpiResult with standard S/E/I/R/D host compartments,
 * so callers (forecast, scenario, backtest) are agnostic to which ran.
 */

import {
  simulateSeird, simulateVectorBorne, isVectorBorneModes, type EpiResult, type BetaScaler,
} from '@biomax/plugin-epidemiology';
import type { Pathogen } from '@biomax/plugin-pathogen-database';

export interface PathogenSimArgs {
  N: number;
  I0: number;
  /** Basic reproduction number for this run (may be a per-ensemble-member draw). */
  r0: number;
  durationDays: number;
  dtDays?: number;
  /** Initial immune fraction as a count (e.g. vaccinated) — the susceptible-depletion lever. */
  R0Pop?: number;
  /** IFR override (e.g. per-ensemble-member draw); defaults to the median-band IFR. */
  ifr?: number;
}

/** Median-band IFR, the value the surfaces feed the simulator. */
export function pathogenIfr(p: Pathogen): number {
  return p.ifrByAgeBand[Math.floor(p.ifrByAgeBand.length / 2)]?.ifr ?? 0.005;
}

export function isVectorBornePathogen(p: Pathogen): boolean {
  return isVectorBorneModes(p.transmission.modes);
}

/** Run the appropriate simulator for `p`. NPI betaScaler is honoured by both. */
export function simulateForPathogen(p: Pathogen, a: PathogenSimArgs, betaScaler?: BetaScaler): EpiResult {
  const gamma = 1 / p.infectiousPeriodDays.value;
  const sigma = 1 / p.latentPeriodDays.value;
  const ifr = a.ifr ?? pathogenIfr(p);
  if (isVectorBornePathogen(p)) {
    return simulateVectorBorne(
      {
        N: a.N, I0: a.I0, R0: a.r0, sigmaH: sigma, gammaH: gamma, ifr,
        durationDays: a.durationDays,
        // Vector FOI is fast — keep the step fine for stability regardless of the caller's dt.
        dtDays: Math.min(a.dtDays ?? 0.25, 0.25),
        R0Pop: a.R0Pop,
      },
      betaScaler ? { betaScaler } : undefined,
    );
  }
  return simulateSeird(
    {
      N: a.N, I0: a.I0, beta: a.r0 * gamma, gamma, sigma, ifr,
      durationDays: a.durationDays, dtDays: a.dtDays, R0Pop: a.R0Pop,
    },
    betaScaler ? { betaScaler } : undefined,
  );
}
